import type { ReactNode } from 'react';
import type { ReportDocument, ReportPhoto } from './report-document';
import { PHOTOS_PER_ROW, reportSections } from './report-document';
import { logoDataUrl } from '../settings/company-logo';
import { useReportPhotoUrl } from './use-report-photo-url';

/**
 * THE CLIENT-FACING REPORT, RENDERED — M10 / S10-03B.
 *
 * **THIS COMPONENT IS THE S10-04 SEAM.** `report-document.ts` decides WHAT the
 * report contains — branding, Project, date, Description, which Captures, which
 * photos, in what order and in which Before/After group. This decides what each
 * of those sections LOOKS like. **Neither knows anything about pages.**
 *
 * **PRESENTATION IS SEPARATE FROM CONTENT, BY JERRY'S DECISION (2026-08-19).**
 * The interactive preview renders this responsively for a phone; S10-04 will
 * render the SAME content model into US Letter PDF pages with print
 * dimensions. Sizes may differ between the two; **what the report contains may
 * not**, and it cannot, because there is one model and one set of section
 * renderers.
 *
 * **EVERY SECTION IS ADDRESSED BY ID**, because that is what lets one page
 * render a subset of them in order without knowing anything about the rest.
 *
 * **NOTHING ABSENT LEAVES A HOLE.** There is no branch here that emits an empty
 * block: a missing logo, a missing contact line, a blank Description and a
 * Capture with no chosen photos each produce no element at all. That is
 * Brandon's rule about wasted space, enforced by the section list rather than
 * by hoping CSS collapses something.
 *
 * **NO CREWLINK MARK.** This document belongs to Brandon's company.
 */

/**
 * The whole report, in ordinary document flow.
 *
 * **NO PAGES, NO FIXED HEIGHT, NO TRANSFORM.** Every section is rendered once,
 * in order, and the element grows to whatever height its content needs — which
 * is what makes the twentieth Capture and the fortieth photo reachable by
 * ordinary scrolling, and what puts the application's "PDF comes next" line
 * physically after the report rather than after a clipped box.
 */
export function ReportDocumentBody({
  document,
  onInspectPhoto,
}: {
  readonly document: ReportDocument;
  readonly onInspectPhoto?: (key: string) => void;
}) {
  return (
    <>
      {reportSections(document).map((section) =>
        renderReportSection(document, section.id, onInspectPhoto),
      )}
    </>
  );
}

function PhotoFrame({
  photo,
  onInspect,
}: {
  readonly photo: ReportPhoto;
  readonly onInspect: ((key: string) => void) | undefined;
}) {
  const { url, onError } = useReportPhotoUrl(photo.view);

  const picture = url ? (
    // **CONTAIN, NEVER COVER.** Cropping construction evidence can remove the
    // damage the photograph exists to prove.
    <img src={url} alt="" onError={onError} />
  ) : (
    <span className="report-doc-photo-blank" />
  );

  // **TAP TO INSPECT, AND NOTHING ELSE.** The preview is read-only: opening a
  // photo cannot remove it, reclassify it, upload it or attach it. When no
  // handler is supplied — the future PDF render — the tile is inert markup.
  if (!onInspect) return <span className="report-doc-photo">{picture}</span>;

  return (
    <button
      type="button"
      className="report-doc-photo report-doc-photo--tappable"
      aria-label="Inspect photo"
      onClick={() => {
        onInspect(photo.key);
      }}
    >
      {picture}
    </button>
  );
}

/**
 * The renderers, one per section id shape. Returning `null` for an unknown id
 * is deliberate: a page asked to draw a section that no longer exists shows
 * nothing rather than throwing away the whole report.
 */
export function renderReportSection(
  document: ReportDocument,
  id: string,
  onInspectPhoto?: (key: string) => void,
): ReactNode {
  if (id === 'branding') {
    const branding = document.branding;
    if (!branding) return null;
    return (
      // **THE CONTACT DETAILS BELONG TO THE COMPANY, AND NOW SIT UNDER IT.**
      // They were previously pushed to the opposite edge of the page, where
      // they read as a separate report field rather than as the way to reach
      // the contractor. Logo, name and contacts are one identity block.
      <header key={id} className="report-doc-branding">
        {branding.logo ? (
          <img
            className="report-doc-logo"
            src={logoDataUrl(branding.logo.mime, branding.logo.base64)}
            alt=""
          />
        ) : null}
        <span className="report-doc-identity">
          <span className="report-doc-company">{branding.companyName}</span>
          {/* Absent fields render NOTHING — no placeholder, no reserved row. */}
          {branding.email ? <span className="report-doc-contact">{branding.email}</span> : null}
          {branding.phone ? <span className="report-doc-contact">{branding.phone}</span> : null}
        </span>
      </header>
    );
  }

  if (id === 'project') {
    // **THE PROJECT IS NOT THE COMPANY**, and they are separate elements so
    // they can never be mistaken for one another on the page.
    return (
      <div key={id} className="report-doc-project">
        <span className="report-doc-project-name">{document.projectName}</span>
        <span className="report-doc-date"> · {document.reportDate}</span>
      </div>
    );
  }

  if (id === 'description') {
    if (!document.description) return null;
    return (
      <p key={id} className="report-doc-description">
        {document.description}
      </p>
    );
  }

  const [captureId, kind, offsetText] = id.split(':');
  const capture = document.captures.find((section) => section.captureId === captureId);
  if (!capture) return null;

  if (kind === 'head') {
    return (
      <div key={id} className="report-doc-capture-head">
        <span className="report-doc-capture-title">{capture.title}</span>
        <span className="report-doc-capture-meta">{capture.meta}</span>
      </div>
    );
  }

  const photos = kind === 'before' ? capture.before : capture.after;
  const offset = Number(offsetText ?? '0');
  const slice = photos.slice(offset, offset + PHOTOS_PER_ROW);
  if (slice.length === 0) return null;

  return (
    <div key={id} className="report-doc-photo-row">
      {/* The label rides with the FIRST row of its class only, so a group
          spanning several rows is not relabelled halfway down. */}
      {offset === 0 ? (
        <span className="report-doc-photo-label">{kind === 'before' ? 'BEFORE' : 'AFTER'}</span>
      ) : null}
      <span className="report-doc-photo-strip">
        {slice.map((photo) => (
          <PhotoFrame key={photo.key} photo={photo} onInspect={onInspectPhoto} />
        ))}
      </span>
    </div>
  );
}
